import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Warehouse, MapPin, Plus, Pen, Trash2, Search, X as IconX, Power, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  warehousesOf, addWarehouse, updateWarehouse, deleteWarehouse,
  zonesOf, findZone,
} from '../../data/store';
import { SideDrawer } from '../../components/SideDrawer';
import { CustomSelect } from '../../components/CustomSelect';
import { formatCategoryName } from '../../utils/formatters';
import { IconAction, RowActions } from '../../components/RowActions';
import { StatusToggle } from '../../components/StatusToggle';

const blank = (orgId, zoneId = '') => ({
  id: null, orgId, zoneId, name: '', address: '', status: 'Active',
});

const Field = ({ label, icon: Icon, error, required, children }) => (
  <label className="block space-y-1">
    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
      {label} {required && <span className="text-rose-500 ml-1">*</span>}
    </span>
    <span className="relative block">
      {Icon && (
        <span className={`absolute left-3 top-1/2 -translate-y-1/2 z-30 pointer-events-none ${error ? 'text-rose-400' : 'text-slate-400'}`} aria-hidden="true">
          <Icon size={14} />
        </span>
      )}
      {React.cloneElement(children, {
        className: `w-full rounded-lg bg-gray-50 border ${error ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-colors ${Icon ? '!pl-9' : ''}`,
      })}
    </span>
    {error && <p className="text-[11px] font-medium text-rose-500 mt-1">{error}</p>}
  </label>
);

Field.propTypes = {
  label: PropTypes.node.isRequired,
  error: PropTypes.string,
  required: PropTypes.bool,
  children: PropTypes.element.isRequired,
};

const WarehouseModal = ({ open, draft, setDraft, onClose, onSubmit, mode, accent, zones, showNotification }) => {
  const [errors, setErrors] = useState({});

  if (!open) return null;

  const handleSubmit = () => {
    const newErrors = {};
    if (!draft.name || !draft.name.trim()) newErrors.name = 'Please fill the Warehouse name.';
    if (!draft.zoneId) newErrors.zoneId = 'Please select a Zone.';
    if (!draft.address || !draft.address.trim()) newErrors.address = 'Please fill the Address.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    onSubmit(draft);
  };
  return (
    <SideDrawer onClose={onClose}>
      <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }}><Warehouse size={18} /></div>
          <div>
            <h3 className="text-lg font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit warehouse' : 'New warehouse'}</h3>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Each warehouse owns one purchase record</p>
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
      </header>
      <div className="px-6 py-5 overflow-y-auto custom-scrollbar grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
        <div className="md:col-span-2">
          <Field label="Warehouse name" icon={Building2} error={errors.name} required>
            <input type="text" value={draft.name} onChange={(e) => {
              setDraft({ ...draft, name: formatCategoryName(e.target.value) });
              if (errors.name) setErrors({ ...errors, name: undefined });
            }} placeholder="e.g. Pondicherry Central WH" />
          </Field>
        </div>
        <Field label="Zone" icon={MapPin} error={errors.zoneId} required>
          <CustomSelect
            value={draft.zoneId || ''}
            onChange={(v) => {
              setDraft({ ...draft, zoneId: v });
              if (errors.zoneId) setErrors({ ...errors, zoneId: undefined });
            }}
            disabled={mode === 'edit'}
            placeholder="Select a zone…"
            options={zones.map((z) => ({ value: z.id, label: z.name }))}
          />
        </Field>
        <Field label="Warehouse Status" error={errors.status}>
          <CustomSelect
            value={draft.status || 'Active'}
            onChange={(val) => setDraft({ ...draft, status: val })}
            options={[
              { label: 'ACTIVE', value: 'Active', icon: <Power size={14} className="text-emerald-500" /> },
              { label: 'INACTIVE', value: 'Inactive', icon: <Power size={14} className="text-rose-500" /> }
            ]}
          />
        </Field>
        <div className="md:col-span-2">
          <Field label="Address" icon={MapPin} error={errors.address} required>
            <input type="text" value={draft.address} onChange={(e) => {
              setDraft({ ...draft, address: e.target.value });
              if (errors.address) setErrors({ ...errors, address: undefined });
            }} placeholder="Street, city" />
          </Field>
        </div>
      </div>
      <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
        <button type="button" onClick={onClose} className="px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
        <button
          type="button"
          onClick={handleSubmit}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-[11px] font-bold uppercase tracking-[0.14em]"
          style={{ background: accent }}
        >
          {mode === 'edit' ? 'Save warehouse' : 'Create warehouse'}
        </button>
      </footer>
    </SideDrawer>
  );
};

WarehouseModal.propTypes = {
  open: PropTypes.bool.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  zones: PropTypes.array.isRequired,
  showNotification: PropTypes.func,
};

export const WarehousesTab = ({ org, showNotification }) => {
  const [version, setVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [draft, setDraft] = useState(blank(org.id));
  const [confirmDelete, setConfirmDelete] = useState(null);

  const zones = useMemo(() => zonesOf(org.id), [org.id, version]);

  const list = useMemo(() => {
    const all = warehousesOf(org.id);
    const filtered = zoneFilter === 'all' ? all : all.filter((w) => w.zoneId === zoneFilter);
    if (!search.trim()) return filtered;
    const q = search.toLowerCase();
    return filtered.filter((w) =>
      (w.name || '').toLowerCase().includes(q) || (w.address || '').toLowerCase().includes(q));
  }, [search, zoneFilter, org.id, version]);

  const accent = org.theme.primary;

  const openCreate = () => {
    const firstZone = zones[0]?.id || '';
    setDraft(blank(org.id, firstZone));
    setModalMode('create');
    setModalOpen(true);
  };
  const openEdit = (w) => { setDraft({ ...w }); setModalMode('edit'); setModalOpen(true); };

  const submit = (record) => {
    if (modalMode === 'create') {
      const id = addWarehouse({
        orgId: org.id,
        zoneId: record.zoneId,
        name: record.name.trim(),
        address: (record.address || '').trim(),
        status: record.status || 'Active',
      });
      if (showNotification) showNotification(`Warehouse "${record.name}" created with its purchase record.`, 'success');
      void id;
    } else {
      updateWarehouse(record.id, {
        name: record.name.trim(),
        address: (record.address || '').trim(),
        status: record.status || 'Active',
      });
      if (showNotification) showNotification('Warehouse updated.', 'success');
    }
    setModalOpen(false);
    setVersion((v) => v + 1);
  };

  const toggleStatus = (w) => {
    const newStatus = w.status === 'Active' ? 'Inactive' : 'Active';
    updateWarehouse(w.id, {
      name: w.name,
      address: w.address,
      status: newStatus,
    });
    if (showNotification) showNotification(`Warehouse marked as ${newStatus}`, 'success');
    setVersion((v) => v + 1);
  };

  const doDelete = () => {
    if (!confirmDelete) return;
    deleteWarehouse(confirmDelete.id);
    if (showNotification) showNotification(`Warehouse "${confirmDelete.name}" removed.`, 'success');
    setConfirmDelete(null);
    setVersion((v) => v + 1);
  };

  if (zones.length === 0) {
    return (
      <div className="bg-white border border-dashed border-[var(--theme-tertiary)] rounded-lg py-16 text-center">
        <Warehouse size={28} className="mx-auto text-slate-300" aria-hidden="true" />
        <p className="mt-3 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">Create a zone first.</p>
        <p className="text-[11px] font-medium text-slate-500 mt-1">Warehouses live inside zones, so you need at least one zone before adding warehouses.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-fit">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search warehouses…"
              aria-label="Search warehouses"
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
            />
          </div>
          <CustomSelect
            value={zoneFilter}
            onChange={(v) => setZoneFilter(v)}
            className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-3 py-2 text-xs font-bold text-[#0d0d0d] outline-none"
            options={[{ value: 'all', label: 'All zones' }, ...zones.map((z) => ({ value: z.id, label: z.name }))]}
          />
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-[11px] font-bold uppercase tracking-[0.14em] transition-colors shadow-lg"
          style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}
        >
          <Plus size={14} aria-hidden="true" /> New warehouse
        </button>
      </section>
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg h-fit">
        <table className="w-full text-left border-collapse">
          <thead>
              <tr className="border-b border-[var(--theme-tertiary)] bg-gray-50/50">
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[25%]">Warehouse Name</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[20%]">Zone</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[30%]">Address</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[10%]">Status</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-right w-[15%]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((w) => {
                const zone = findZone(w.zoneId);
                return (
                  <tr key={w.id} className="border-b border-[var(--theme-tertiary)] last:border-0 hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <p className="text-[13px] font-extrabold text-[#0d0d0d]">{w.name}</p>
                    </td>
                    <td className="px-5 py-3">
                      {zone ? (
                        <p className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide">{zone.name}</p>
                      ) : (
                        <p className="text-[12px] text-slate-400">—</p>
                      )}
                    </td>
                    <td className="px-5 py-3 max-w-[200px]">
                      <p className="text-[11px] font-medium text-slate-500 truncate" title={w.address}>{w.address || '—'}</p>
                    </td>
                    <td className="px-5 py-3">
                      <StatusToggle
                        on={(w.status || 'Active') === 'Active'}
                        label={w.name}
                        onChange={() => toggleStatus(w)}
                      />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <RowActions>
                        <IconAction icon={Pen} label={`Edit ${w.name}`} title="Edit" onClick={() => openEdit(w)} />
                        <IconAction
                          icon={Trash2}
                          tone="danger"
                          label={`Remove ${w.name}`}
                          title="Remove"
                          onClick={() => setConfirmDelete(w)}
                        />
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-5 py-16 text-center text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
                    No warehouses yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      <WarehouseModal
        open={modalOpen}
        draft={draft}
        setDraft={setDraft}
        onClose={() => setModalOpen(false)}
        onSubmit={submit}
        mode={modalMode}
        accent={accent}
        zones={zones}
        showNotification={showNotification}
      />

      {confirmDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }}><Trash2 size={20} /></div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove {confirmDelete.name}?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">The warehouse and its purchase record will be removed. Branches sourcing from it will be detached.</p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setConfirmDelete(null)} className="flex-1 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={doDelete} className="flex-1 py-3 text-[11px] font-bold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

WarehousesTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
